<template>
  <AppSection
    id="sms-templates"
    :title="sectionTitle"
    type="sub-section-1"
  >

    <b-overlay :show="isLoading || isSubmitting || isTesting">

      <template #overlay>
        <AppSpinner size="5" class="text-secondary"/>
      </template>

      <TemplatesSlide :type="type" @onSelect="getTemplateTranslations"/>
      <hr class="w-75 mb-4"/>

      <b-row>

        <b-col v-if="!templates.length && !isLoading">
          <h4 class="text-center">{{$t($getConst.pages.sms_configuration.sms_no_template_selected_message) | capitalize}}</h4>
        </b-col>

        <template v-else>
          <b-col md="6">
            <b-form-group id="template-language" :label="$t($getConst.pages.sms_configuration.sms_template_language_label)" label-for="template-language-input">
              <multiselect
                class="border-right-0 border-left-0"
                v-model="language"
                track-by="id"
                label="name"
                :placeholder="$t(this.$getConst.pages.form.select_option)"
                :options="languages"
                :disabled="isSubmitting || isLoading || isEstimating"
                :allowEmpty="false"
                @select="selectTemplate"
              >
                <template slot="singleLabel" slot-scope="props">
                  <lang-flag :iso="props.option.code" :squared="false" class="mr-2"/>
                  {{$t(props.option.name) | capitalize}}
                </template>
                <template slot="option" slot-scope="props">
                  <lang-flag :iso="props.option.code" :squared="false" class="mr-2"/>
                  {{$t(props.option.name.toLowerCase()) | capitalize}}
                </template>
                <span slot="noResult">{{$t($getConst.pages.alerts.no_results_found)}}</span>
              </multiselect>
            </b-form-group>

            <b-form-group
              id="sms-text"
              :label="$t($getConst.pages.sms_configuration.sms_text_label) + ' *'"
              label-for="sms-text"
            >
              <b-form-textarea
                id="sms-text-input"
                v-model.trim="$v.text.$model"
                :class="{ 'is-invalid': $v.text.$error }"
                :placeholder="$t($getConst.pages.sms_configuration.sms_text_placeholder) + ' *'"
                :disabled="isEstimating"
                rows="5"
                size="lg"
                required
              ></b-form-textarea>
              <b-form-text>{{$t($getConst.pages.sms_configuration.sms_characters_number) + ': '}}{{text ? text.length : 0}}</b-form-text>
              <AppFormFeedback :errors="$v.text"/>
            </b-form-group>

            <hr class="my-4"/>

            <b-form-group
              id="sms-number"
              label-for="sms-number-input"
              :label="$t($getConst.pages.sms_configuration.sms_number) + ': '"
            >
              <b-input-group>
                <b-form-input id="sms-number-input" :value="nbOfSms" disabled/>
                <b-input-group-append>
                  <b-button variant="success" :disabled="$v.text.$invalid || isEstimating" @click="estimateSms">
                    <AppSpinner class="d-inline-block align-middle" v-if="isEstimating"/>
                    {{$t($getConst.pages.sms_configuration.sms_button_calculate) | capitalize}}
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>

          </b-col>

          <b-col md="6">
            <SmsTemplateOverview :sms-text="text"/>
          </b-col>
        </template>

      </b-row>

      <AppConfirmButtons
        v-if="!isLoading && templates.length"
        :first-button="hasPermission(this.$getConst.permissions.writeSmsTemplate)"
        :second-button="hasPermission(this.$getConst.permissions.readSmsTemplate)"
        :second-button-text="this.$getConst.pages.sms_configuration.sms_test_button"
        :is-loading-second-button="isTesting"
        no-cancel
        :is-loading="isSubmitting"
        @onSubmit="save"
        @onSecondSubmit="testTemplate"
        :is-disabled="$v.text.$invalid || isEstimating"
        :second-button-disabled="$v.text.$invalid || isEstimating"
      />

    </b-overlay>

  </AppSection>
</template>

<script>
  import AppSection from "../../../../../components/common/sections/AppSection";
  import TemplatesSlide from "./templatesSlide";
  import AppFormFeedback from "../../../../../components/common/form/AppFormFeedback";
  import {required} from "vuelidate/lib/validators";
  import SmsTemplateOverview from "./smsTemplateOverview";
  import AppSpinner from "../../../../../components/common/utils/AppSpinner";
  import AppConfirmButtons from "../../../../../components/common/utils/AppConfirmButtons";
  import {
    estimateSms,
    getTemplatesByScopeAndType,
    setSmsTemplate,
    testSmsTemplate
  } from "../../../../../api/smsTemplates.api";

  export default {
    name: "index",
    components: {AppConfirmButtons, AppSpinner, SmsTemplateOverview, AppFormFeedback, TemplatesSlide, AppSection},
    props: {
      scope_id: null,
      type: null,
    },
    data() {
      return {
        templates: [],
        templateType: null,
        isLoading: false,
        isSubmitting: false,
        isTesting: false,
        isEstimating: false,
        id: null,
        text: null,
        languages: [],
        language: null,
        nbOfSms: null
      }
    },
    validations: {
      text: {required}
    },
    computed: {
      sectionTitle() {
        if (this.type === 'timesheets') {
          return this.$t(this.$getConst.pages.sms_configuration.timesheets_sms_templates);
        } else if (this.type === 'freelancer') {
          return this.$t(this.$getConst.pages.sms_configuration.freelancer_sms_templates);
        } else {
          return this.$t(this.$getConst.pages.sms_configuration.sms_template_page_title);
        }
      }
    },
    methods: {
      getTemplateTranslations(type) {
        this.templateType = type;
        this.isLoading = true;
        getTemplatesByScopeAndType(this.scope_id, type).then(
          response => {
            this.templates = response.data;
            this.language = response.data[0].language;
            this.id = response.data[0].id;
            this.text = response.data[0].text;
            this.languages = [];
            this.templates.forEach(template => {
              this.languages.push(template.language);
            })
            this.isLoading = false;
            this.estimateSms();
          },
          (error) => {
            this.isLoading = false;
            this.handleApiError(error);
          }
        )
      },
      save() {
        this.isSubmitting = true;
        setSmsTemplate(this.scope_id, this.id, this.text).then(
          () => {
            this.isSubmitting = false;
            this.$snotify.success(this.$t(this.$getConst.pages.alerts.success_update));
          },
          (error) => {
            this.handleApiError(error);
            this.isSubmitting = false;
          }
        )
      },
      selectTemplate(language) {
        const smsTemplate = this.templates.find(template => template.language.id === language.id);
        this.id = smsTemplate.id;
        this.text = smsTemplate.text;
        this.estimateSms();
      },
      estimateSms() {
        this.isEstimating = true;
        this.nbOfSms = null;
        estimateSms(this.text, this.scope_id).then(
          (response) => {
            this.nbOfSms = response.data.parts;
            this.isEstimating = false;
          },
          (error) => {
            this.isEstimating = false;
            this.handleApiError(error);
          }
        )
      },
      testTemplate() {
        const phone = this.$store.getters["auth/phone"];
        if (!phone) {
          this.$snotify.error(
            this.$t(this.$getConst.pages.sms_configuration.sms_no_phone_error),
            this.$t(this.$getConst.pages.alerts.error)
          );
        } else {
          this.isTesting = true;
          testSmsTemplate(this.scope_id, this.text, this.language.id).then(
            () => {
              this.$snotify.success(this.$t(this.$getConst.pages.alerts.success_operation));
              this.isTesting = false;
            },
            (error) => {
              this.handleApiError(error);
              this.isTesting = false;
            }
          )
        }
      }
    },
    watch: {
      text() {
        this.nbOfSms = null;
      }
    }
  }
</script>

<style scoped>
  #sms-number-input {
    border-bottom-right-radius: 0 !important;
    border-top-right-radius: 0 !important;
  }
</style>
